/* eslint-disable no-param-reassign */

import {
  operatorSeparator,
  getOperatorResolver,
  getBooleanResolver,
  ifFunctionResolver,
  modFunctionResolver,
  isBlankFunctionResolver,
  getConditionResolver,
  removeParenthesisResolver,
  optionFilterResolver,
} from './expressionResolvers';

export const inputTagNames = ['input', 'upload', 'repeat', 'select1', 'select'];

export const tagNameToFieldType = {
  select1: 'select',
  select: 'select',
};

export const groupTagNames = ['repeat'];

export const expressionFunctionMappings = [
  { regex: /([\d\w"])([-+])([\d\w"])/g, resolverFunction: operatorSeparator },
  { regex: /\s!=\s/g, resolverFunction: getOperatorResolver(' <> ') },
  { regex: /\sdiv\s/g, resolverFunction: getOperatorResolver(' / ') },
  { regex: /true\(\)/g, resolverFunction: getBooleanResolver('true') },
  { regex: /false\(\)/g, resolverFunction: getBooleanResolver('false') },
  {
    regex: /\s!=\s/g,
    resolverFunction: getConditionResolver('or', 'OR'),
  },
  {
    regex: /\(.+\sor\s.+\)/g,
    resolverFunction: getConditionResolver('or', 'OR'),
  },
  {
    regex: /\(.+\sor\s.+\)/g,
    resolverFunction: getConditionResolver('or', 'OR'),
  },
  {
    regex: /\(.+\sand\s.+\)/g,
    resolverFunction: getConditionResolver('and', 'AND'),
  },
  { regex: /get\(/g, newFunctionName: 'GET' },
  { regex: /min\(/g, newFunctionName: 'MIN' },
  { regex: /max\(/g, newFunctionName: 'MAX' },
  { regex: /ave\(/g, newFunctionName: 'AVERAGE' },
  { regex: /round\(/g, newFunctionName: 'ROUND' },
  { regex: /power\(/g, newFunctionName: 'POWER' },
  { regex: /number\(/g, newFunctionName: 'TO_NUMBER' },
  { regex: /sum\(/g, newFunctionName: 'SUM' },
  {
    regex: /if\s?\(.*\)\s?then\s?\(.*\)/g,
    resolverFunction: ifFunctionResolver,
  },
  { regex: /mod\s?\(/g, resolverFunction: modFunctionResolver },
  { regex: /concat\(/g, newFunctionName: 'CONCATENATE' },
  { regex: /uppercase\(/g, newFunctionName: 'UPPERCASE' },
  { regex: /lowercase\(/g, newFunctionName: 'LOWERCASE' },
  { regex: /strlen\(/g, newFunctionName: 'LENGTH' },
  { regex: /substring\(/g, newFunctionName: 'SUBSTRING' },
  { regex: /dm_timestamp\(/g, newFunctionName: 'TIMESTAMP' },
  { regex: /dm_geostamp\(/g, newFunctionName: 'GEOSTAMP' },
  { regex: /dm_latitude\(/g, newFunctionName: 'LATITUDE' },
  { regex: /dm_longitude\(/g, newFunctionName: 'LONGITUDE' },
  { regex: /dm_device_identifier\(/g, newFunctionName: 'DEVICE_IDENTIFIER' },
  { regex: /urlencode\(/g, newFunctionName: 'URL_ENCODE' },
  { regex: /keyvalue\(/g, newFunctionName: 'KEY_VALUE' },
  { regex: /count\(/g, newFunctionName: 'COUNT' },
  { regex: /index_of\(/g, newFunctionName: 'INDEX_OF' },
  { regex: /index\(/g, newFunctionName: 'INDEX' },
  { regex: /not\(hasvalue\(/g, resolverFunction: isBlankFunctionResolver },
  { regex: /hasvalue\(/g, newFunctionName: 'NOTBLANK' },
  { regex: /floor\(/g, newFunctionName: 'FLOOR' },
  { regex: /ceil\(/g, newFunctionName: 'CEIL' },
  { regex: /min\(/g, newFunctionName: 'MIN' },
  { regex: /max\(/g, newFunctionName: 'MAX' },
  { regex: /ave\(/g, newFunctionName: 'AVERAGE' },
  { regex: /round\(/g, newFunctionName: 'ROUND' },
  { regex: /power\(/g, newFunctionName: 'POWER' },
  { regex: /number\(/g, newFunctionName: 'TO_NUMBER' },
  { regex: /sum\(/g, newFunctionName: 'SUM' },
  { regex: /concat\(/g, newFunctionName: 'CONCATENATE' },
  { regex: /uppercase\(/g, newFunctionName: 'UPPERCASE' },
  { regex: /lowercase\(/g, newFunctionName: 'LOWERCASE' },
  { regex: /strlen\(/g, newFunctionName: 'LENGTH' },
  { regex: /substring\(/g, newFunctionName: 'SUBSTRING' },
  { regex: /dm_timestamp\(/g, newFunctionName: 'TIMESTAMP' },
  { regex: /dm_geostamp\(/g, newFunctionName: 'GEOSTAMP' },
  { regex: /dm_latitude\(/g, newFunctionName: 'LATITUDE' },
  { regex: /dm_longitude\(/g, newFunctionName: 'LONGITUDE' },
  { regex: /dm_device_identifier\(/g, newFunctionName: 'DEVICE_IDENTIFIER' },
  { regex: /urlencode\(/g, newFunctionName: 'URL_ENCODE' },
  { regex: /keyvalue\(/g, newFunctionName: 'KEY_VALUE' },
  { regex: /count\(/g, newFunctionName: 'COUNT' },
  { regex: /index_of\(/g, newFunctionName: 'INDEX_OF' },
  { regex: /index\(/g, newFunctionName: 'INDEX' },
  { regex: /^\(.*\)$/, resolverFunction: removeParenthesisResolver },
];

const inputTypeMappings = {
  'dm:gpsLocation': 'location',
  'dm:email': 'email',
  'dm:phoneNumber': 'phone_number',
  'dm:barcode': 'barcode',
  'dm:license': 'license',
  'dm:password': 'password',
  'dm:resource': 'resource',
  'dm:webservice': 'webservice',
};

export const attributeMappings = {
  readonly: {
    newName: 'readonly_rule',
    valueMappingFunction: ({ value, inputRef, transformExpression }) => {
      if (value === 'true()') return 'always';
      inputRef.readonly_expr = transformExpression(value);
      return 'when';
    },
  },
  required: {
    newName: 'required_rule',
    valueMappingFunction: ({ value, inputRef, transformExpression }) => {
      if (value === 'true()') return 'always';
      inputRef.required_expr = transformExpression(value);
      return 'when';
    },
  },
  calculate: {
    newName: 'calculate_expr',
    isExpression: true,
  },
  relevant: {
    newName: 'visible_expr',
    isExpression: true,
  },
  constraint: {
    newName: 'validate_expr',
    isExpression: true,
  },
  'dm:constraintError': {
    newName: 'validate_msg',
  },
  mediatype: {
    newName: 'type',
    valueMappings: {
      'image/*': 'image',
      'image/vnd.devicemagic.signature': 'signature',
      'image/vnd.devicemagic.sketch': 'sketch',
    },
  },
  'dm:dataBindingResource': {
    newName: 'dataBindingResource',
  },
  'dm:dataBindingTable': {
    newName: 'dataBindingTable',
  },
  'dm:dataBindingColumn': {
    newName: 'dataBindingColumn',
  },
  'dm:calculatedInitialAnswer': {
    newName: 'initialAnswerExpr',
    isExpression: true,
  },
  'dm:preserveOriginalSize': {
    newName: 'extra_large',
    parseBooleans: true,
  },
  'dm:cameraOnly': {
    newName: 'camera_only',
    parseBooleans: true,
  },
  'dm:autoResolve': {
    newName: 'auto_resolve',
    parseBooleans: true,
  },
  'dm:allowManualOverride': {
    newName: 'allow_manual_override',
    parseBooleans: true,
  },
  'dm:overlayResource': {
    newName: 'overlay_resource',
  },
  'dm:multiline': {
    newName: 'multi_line',
    parseBooleans: true,
  },
  'dm:validateFormat': {
    newName: 'validate_format',
    parseBooleans: true,
  },
  'dm:barcodeType': {
    newName: 'barcode_type',
  },
  'dm:experimentalScanner': {
    newName: 'experimental_scanner',
    parseBooleans: true,
  },
  'dm:disableFormatting': {
    newName: 'disable_formatting',
    parseBooleans: true,
  },
  'dm:resourceIdentifier': {
    newName: 'resource_identifier',
  },
  'dm:optionsResource': {
    newName: 'options_resource',
  },
  'dm:optionsTable': {
    newName: 'options_table',
  },
  'dm:optionsIdentifierColumn': {
    newName: 'options_identifier_column',
  },
  'dm:optionsTextColumn': {
    newName: 'options_text_column',
  },
  'dm:optionsFilter': {
    newName: 'options_filter_expr',
    valueMappingFunction: ({ value, transformExpression, inputRef }) => {
      return optionFilterResolver(
        undefined,
        transformExpression(value, inputRef.identifier)
      );
    },
  },
  'dm:backgroundResource': {
    newName: 'background_resource',
  },
  'dm:url': {
    newName: 'url_expr',
    isExpression: true,
  },
  'dm:minOccurs': {
    newName: 'minOccurs',
    parseInt: true,
  },
  'dm:maxOccurs': {
    newName: 'maxOccurs',
    parseInt: true,
  },
  'dm:timestamp': {
    newName: 'timestamp',
    parseBooleans: true,
  },
  'dm:geostamp': {
    newName: 'geostamp',
    parseBooleans: true,
  },
  type: {
    cloneAttributeTo: ['type_derived'],
    valueMappings: inputTypeMappings,
  },
  'dm:answerSummary': {
    newName: 'answer_summary',
    valueMappingFunction: ({ value, inputRef, transformExpression }) => {
      return inputRef.answer_summary = transformExpression(value);
    },
  }
};

export const tagNameToAttributes = {
  // If the tag name is "select", means that it is a multiple select since
  // a normal select has "select1" as tag name
  select: {
    multiple: true,
  },
};

export const formAttributeMappings = {
  xmlns: { newName: 'namespace' },
  dependentResources: {
    valueMappingFunction: ({ value }) => (value ? value.split(',') : undefined),
  },
  'dm:location': { newName: 'location' },
  'dm:address': { newName: 'address' },
  'dm:scheduled_at': { newName: 'scheduledAt' },
  'dm:geofence_radius': { newName: 'geoFenceRadius' },
};

export const dispatchTypes = {
  GEO_LOCATED: 'Geolocated',
  GEO_FENCED: 'Geofenced',
  REGULAR: 'Regular',
};

export const childAttributeMappings = {
  label: {
    attributeName: 'title',
  },
  hint: {
    attributeName: 'hint',
  },
  item: {
    attributeName: 'options',
    children: {
      label: { attributeName: 'text' },
      value: { attributeName: 'identifier' },
    },
  },
};

export const attributeIgnore = {
  ref: true,
  nodeset: true,
};
